((root, factory) => {
  const dependencies = typeof module === 'object' && module.exports
    ? {
        intelligence: require('./corkopia-intelligence-layer.js'),
        habitat: require('./corkopia-habitat-engine.js'),
        build: require('./corkopia-build-advisor.js')
      }
    : {
        intelligence: root.CorkopiaIntelligenceLayer,
        habitat: root.CorkopiaHabitatEngine,
        build: root.CorkopiaBuildAdvisor
      };
  const api = factory(dependencies);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaCustomerAgents = api;
})(typeof globalThis !== 'undefined' ? globalThis : window, (dependencies) => {
  'use strict';

  const { intelligence, habitat, build } = dependencies;
  const VERSION = 'customer-agent-services-v1';
  const CONFIDENCE = Object.freeze(['High', 'Medium', 'Low', 'Unknown']);
  const confidence = (value) => CONFIDENCE.includes(value) ? value : 'Unknown';
  const publicProduct = (product) => {
    const identity = habitat.productIdentity(product);
    return {
      pieceId: identity.pieceId,
      productId: identity.productId,
      variantId: identity.variantId,
      handle: identity.handle,
      title: identity.title,
      family: identity.family,
      image: identity.image,
      priceCents: identity.priceCents,
      dimensions: habitat.productDimensions(product)
    };
  };

  const envelope = (advisorId, request, body) => ({
    contractVersion: 'advisor-service-v1',
    advisorId,
    serviceVersion: VERSION,
    requestId: request.requestId || `${advisorId}-local`,
    input: request,
    output: body.output,
    reasoning: {
      why: body.why,
      confidence: confidence(body.confidence),
      ruleSources: body.ruleSources || [],
      inventorySources: body.inventorySources || [],
      futureAiNotes: body.futureAiNotes || ['A future provider may assist interpretation but cannot override rules, eligibility, or fit.']
    },
    actions: body.actions || [],
    events: body.events || [],
    telemetry: {
      destination: null,
      sent: false,
      latencyMs: null,
      provider: 'DETERMINISTIC_LOCAL'
    }
  });

  const habitatAgent = Object.freeze({
    id: 'habitat',
    run(request = {}) {
      const pipeline = intelligence.execute({
        request,
        handlers: {
          input: () => ({ value: request, codes: ['STRUCTURED_INPUT_RECEIVED'] }),
          validation: () => {
            const usableSpace = habitat.calculateUsableSpace(request.enclosure || {});
            return {
              value: { valid: usableSpace.status === 'READY', usableSpace },
              status: usableSpace.status === 'READY' ? 'complete' : 'failed',
              codes: usableSpace.status === 'READY' ? ['ENCLOSURE_CONFIRMED'] : usableSpace.reasonCodes
            };
          },
          rules: (context) => ({
            value: {
              fitAuthority: 'deterministic_fit_engine',
              aiMayOverrideFit: false,
              speciesProfileAuthority: 'fixture_unapproved'
            },
            codes: ['AUTHORITIES_ENFORCED']
          }),
          eligibility: () => {
            const eligible = (request.products || []).filter(habitat.publicEligible);
            return {
              value: eligible,
              status: eligible.length ? 'complete' : 'empty',
              codes: eligible.length ? ['CURRENT_EXACT_PIECES_ONLY'] : ['NO_ELIGIBLE_INVENTORY']
            };
          },
          ranking: (context) => ({
            value: context.eligibility,
            status: context.eligibility.length ? 'complete' : 'empty',
            codes: ['DETERMINISTIC_PUBLIC_ORDER']
          }),
          recommendation: (context) => ({
            value: habitat.generateLayout({
              products: context.ranking,
              usableSpace: context.validation.usableSpace,
              goals: request.interview?.goals || [],
              budgetCents: request.interview?.budgetCents,
              maximumPieces: request.maximumPieces || 8
            }),
            codes: ['SCALED_LAYOUT_CREATED']
          }),
          explanation: (context) => ({
            value: context.recommendation.explanation,
            codes: ['RULE_AND_INVENTORY_SOURCES_EXPOSED']
          }),
          actions: (context) => ({
            value: context.recommendation.placements.map((placement) => ({
              id: `inspect-${placement.variantId}`,
              type: 'INSPECT_EXACT_PIECE',
              subject: { variantId: placement.variantId },
              cartMutation: false,
              customerConfirmationRequired: false
            })),
            codes: ['LOCAL_ACTIONS_PREPARED']
          }),
          events: (context) => ({
            value: [habitat.createEvent('customer.habitat.layout_generated', {
              layoutId: context.recommendation.layoutId,
              fitStatus: context.recommendation.fit.status,
              pieceCount: context.recommendation.placements.length
            })],
            codes: ['EVENTS_PREPARED_NOT_SENT']
          }),
          logging: (context) => ({
            value: {
              destination: null,
              sent: false,
              advisorId: 'habitat',
              outcome: context.recommendation.inventoryStatus
            },
            codes: ['TELEMETRY_NOT_CONNECTED']
          })
        }
      });
      const layout = pipeline.context.recommendation || habitat.generateLayout();
      return {
        ...envelope('habitat', request, {
          output: {
            layout,
            publicPieces: layout.placements.map((placement) => ({
              variantId: placement.variantId,
              pieceId: placement.pieceId,
              fit: placement.fit
            }))
          },
          why: layout.explanation.why,
          confidence: layout.explanation.confidence,
          ruleSources: layout.explanation.ruleSources,
          inventorySources: layout.explanation.inventorySources,
          actions: pipeline.context.actions,
          events: pipeline.context.events
        }),
        stages: pipeline.stages,
        authority: {
          rules: 'corkopia_rule_engine',
          inventory: 'current_public_eligibility',
          fit: 'deterministic_fit_engine',
          provider: 'none',
          cart: 'shopify'
        }
      };
    }
  });

  const buildAgent = Object.freeze({
    id: 'build',
    run(request = {}) {
      const existing = build.run(request);
      return envelope('build', request, {
        output: existing,
        why: existing.explanation?.summary || 'The deterministic Build Advisor evaluated current eligible inventory.',
        confidence: existing.outcome === 'recommended' ? 'Medium' : 'Unknown',
        ruleSources: [existing.rule_set_version].filter(Boolean),
        inventorySources: [existing.authority?.catalog].filter(Boolean),
        actions: existing.proposed_actions,
        events: existing.events
      });
    }
  });

  const searchAgent = Object.freeze({
    id: 'search',
    run(request = {}) {
      const terms = String(request.query || '').toLowerCase().split(/\s+/).filter(Boolean);
      const products = (request.products || []).filter(habitat.publicEligible);
      const ranked = products.map((product) => {
        const subject = publicProduct(product);
        const haystack = [subject.title, subject.family, ...(product.compatibility?.buildRoles || [])].join(' ').toLowerCase();
        return { subject, score: terms.reduce((sum, term) => sum + (haystack.includes(term) ? 1 : 0), 0) };
      }).filter((entry) => !terms.length || entry.score > 0)
        .sort((a, b) => b.score - a.score || a.subject.variantId.localeCompare(b.subject.variantId));
      return envelope('search', request, {
        output: {
          results: ranked.map((entry) => entry.subject),
          outcome: ranked.length ? 'RESULTS' : 'EMPTY'
        },
        why: ranked.length
          ? 'Results match the customer’s structured terms and current public eligible exact pieces.'
          : 'No current eligible exact piece matches these terms.',
        confidence: ranked.length && terms.length ? 'Medium' : ranked.length ? 'Low' : 'Unknown',
        ruleSources: ['structured_public_search_v1'],
        inventorySources: ['current_shopify_public_projection'],
        actions: ranked.map((entry) => ({
          type: 'OPEN_EXACT_PIECE',
          subject: { variantId: entry.subject.variantId },
          cartMutation: false
        })),
        events: [habitat.createEvent('customer.search.completed', { resultCount: ranked.length })]
      });
    }
  });

  return Object.freeze({
    VERSION,
    CONFIDENCE,
    buildAgent,
    searchAgent,
    habitatAgent
  });
});
